import { STEP_BY_CODE, BUILDER_STEPS } from "../builder/builderSteps";

/**
 * What My Books says about a book (docs/design/HANDOFF.md, "My Books").
 * Pure: a book (as the API lists it) and the ids in the local cart go in,
 * the card's words come out.
 *
 *   draft    In progress     "Step 3 of 5 · Started Oct 2"      Continue
 *   ready    Ready to order  "Cover + 20 pages · Edited today"  Add to order
 *            (a PDF-only book reads "Printable PDF · Edited …" and carries the "PDF only" tag;
 *            since 2026-10-26 it goes through the cart like any book)
 *   cart     In your cart    "Cover + 20 pages · Edited today"  View cart
 *   ordered  Ordered         "Ordered Dec 3, 2025 · Book + PDF" Order again
 *            (bought as a PDF: Download PDF + Get it printed, no Order again; printed
 *            only: Order again + Get the PDF; designs BooksOrdered)
 *   gift     In progress     (a gift book still being made)     Go to your gift
 */

const PAGES = 20;
// Roughly how far along a draft looks on the card's thin bar, per step.
const STEP_PCT = { 1: 10, 2: 30, 3: 60, 4: 80, 5: 95 };

export function stepNumberOf(book) {
  const latest = book?.latestStepCode;
  // Trimmed: the API's global step list spells step 4 "Design_Your_Cover_Page " (trailing space).
  const code = String((typeof latest === "object" ? latest?.code : latest) || "").trim();
  if (code === "Place_Order") return 5;
  return STEP_BY_CODE[code] || 1;
}

/** The builder route for the step a book is on. */
export function routeForBook(book) {
  const n = stepNumberOf(book);
  const step = BUILDER_STEPS[n - 1] || BUILDER_STEPS[0];
  return `${step.route}?stepCode=${step.stepCode}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "today", "yesterday", "Oct 12", or "Dec 3, 2025" for another year. */
export function shortDate(value, now = new Date()) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((dayStart(now) - dayStart(d)) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  const base = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function describeBook(book, { cartBookIds = new Set(), now = new Date() } = {}) {
  const isPdf = book?.tag === "downloadPdf";
  const hasCover = !!(book?.coverImageThumbnail || book?.coverImage);
  const step = stepNumberOf(book);
  const completed = (book?.completedSteps || []).map((s) => (typeof s === "object" ? s?.code : s));
  const edited = shortDate(book?.updatedAt, now);
  // Every printed book has 20 interior pages (blanks fill any gaps). The API's
  // pageCount can't be shown: it counts the cover and never goes down when a
  // photo is removed, so it can't give the "12 of 20 photos" the mockup draws
  // either (docs/design/GAPS.md).
  const pagesText = `Cover + ${PAGES} pages`;

  if (book?.isOrderPlaces) {
    const when = shortDate(book.lastOrderCreatedAt || book.updatedAt, now);
    // What was bought, from the orders (`orderedFormat`), never the tag: buying the other
    // format switches the tag. The tag stands in only when the API sends no orderedFormat.
    // A PDF-only order also has a downloadable PDF, so the format decides first.
    const bought = book.orderedFormat || (isPdf ? "pdf" : "book");
    const ownsPdf = bought !== "book" || !!book.consumerPdfOrderId;
    return {
      status: "ordered",
      label: "Ordered",
      meta: `Ordered ${when} · ${bought === "pdf" ? "PDF" : ownsPdf ? "Book + PDF" : "Book"}`,
      // Buying the same PDF twice makes no sense, so a PDF order has no Order again.
      action: bought === "pdf" ? null : "Order again",
      isPdf: bought === "pdf",
      hasPdfDownload: !!book.consumerPdfOrderId,
      // The format they don't have yet: "print" (Get it printed) or "pdf" (Get the PDF).
      otherFormat: bought === "pdf" ? "print" : ownsPdf ? null : "pdf",
    };
  }
  if (book?.gift?.giftOrderId) {
    return { status: "gift", label: "In progress", meta: `${pagesText}${edited ? ` · Edited ${edited}` : ""}`, action: "Go to your gift", isPdf: false };
  }
  const whatText = isPdf ? "Printable PDF" : pagesText;
  if (cartBookIds.has(String(book?._id))) {
    return { status: "cart", label: "In your order", meta: `${whatText}${edited ? ` · Edited ${edited}` : ""}`, action: "View order", isPdf };
  }
  const ready = hasCover && (step >= 5 || completed.includes("Review_Your_Book"));
  if (ready) {
    return {
      status: "ready",
      label: "Ready to order",
      meta: `${whatText}${edited ? ` · Edited ${edited}` : ""}`,
      action: "Add to order",
      isPdf,
    };
  }
  const started = shortDate(book?.createdAt, now);
  return {
    status: "draft",
    label: "In progress",
    // TODO(design): the mockup's "12 of 20 photos" needs a live photo count from the API.
    meta: `Step ${step} of 5${started ? ` · Started ${cap(started)}` : ""}`,
    pct: STEP_PCT[step] || 10,
    action: "Continue",
    isPdf,
  };
}

export const FILTERS = [
  { key: "all", label: "All" },
  { key: "progress", label: "In progress" },
  { key: "ordered", label: "Ordered" },
];

export function matchesFilter(status, filter) {
  if (filter === "all") return true;
  return filter === "ordered" ? status === "ordered" : status !== "ordered";
}
